import { useId } from 'react'
import BlueprintGrid from './BlueprintGrid'
import GcodeDemo from './GcodeDemo'

// Part drawn at 2.6 px/mm, origin at (60, 100): 120 × 80 mm plate, two 38 × 18 pockets, four Ø6.8 holes.
const S = 2.6
const X0 = 60
const Y0 = 100
const mm = (x: number, y: number) => [X0 + x * S, Y0 + y * S] as const
const POCKETS = [mm(12, 12), mm(70, 12)]
const HOLES = [25, 50, 75, 100].map(x => mm(x, 55))
const R_HOLE = 3.4 * S

// Toolpaths: a zigzag clearing each pocket, then the outside profile offset by the tool radius.
const zigzag = (px: number, py: number) => {
  const [x1, x2] = [px + 6, px + 38 * S - 6]
  const rows = [6, 14, 22, 30, 38].map(dy => py + dy + 1)
  return rows.map((y, i) => `${i === 0 ? 'M' : 'V'}${i === 0 ? `${x1} ${y}` : y} H${i % 2 === 0 ? x2 : x1}`).join(' ')
}
const PROFILE = `M${X0 - 8} ${Y0 - 8} H${X0 + 120 * S + 8} V${Y0 + 80 * S + 8} H${X0 - 8} Z`

/** A callout tag: leader from a feature to a boxed mono label (Ramp-style annotation). */
function Callout({ from, at, text, tone = 'cyan' }: { from: readonly [number, number]; at: readonly [number, number]; text: string; tone?: 'cyan' | 'amber' }) {
  const stroke = tone === 'cyan' ? '#22d3ee' : '#fbbf24'
  const width = text.length * 6.6 + 16
  return (
    <g className="max-sm:hidden">
      <path d={`M${from[0]} ${from[1]} L${at[0] - 14} ${at[1]} H${at[0]}`} stroke={stroke} strokeOpacity="0.55" />
      <circle cx={from[0]} cy={from[1]} r="2" fill={stroke} />
      <rect x={at[0]} y={at[1] - 10} width={width} height="20" rx="3" fill="#030712" stroke={stroke} strokeOpacity="0.5" />
      <text x={at[0] + 8} y={at[1] + 3.5} fontSize="10" letterSpacing="0.6" className={`font-mono ${tone === 'cyan' ? 'fill-cyan-300' : 'fill-amber-300'}`}>{text}</text>
    </g>
  )
}

function PartDrawing() {
  const arrow = `hw-arrow-${useId().replace(/:/g, '')}`
  const [right, bottom] = mm(120, 80)
  return (
    // Below sm the callouts are hidden, so the SVG is drawn 1.5× wide and the empty right third is clipped: the part fills the pane.
    <svg viewBox="0 0 600 400" className="h-auto w-full max-sm:w-[150%] max-sm:max-w-none" fill="none" role="img"
      aria-label="Drawing of a 120 by 80 millimetre bracket with two pockets and four holes, annotated with recognised features and toolpaths">
      <defs>
        <marker id={arrow} viewBox="0 0 10 10" refX="10" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 z" fill="#22d3ee" />
        </marker>
      </defs>

      {/* Dimensions */}
      <g stroke="#475569">
        <line x1={X0} y1={Y0 - 6} x2={X0} y2={Y0 - 38} />
        <line x1={right} y1={Y0 - 6} x2={right} y2={Y0 - 38} />
        <line x1={X0 - 6} y1={Y0} x2={X0 - 38} y2={Y0} />
        <line x1={X0 - 6} y1={bottom} x2={X0 - 38} y2={bottom} />
      </g>
      <line x1={X0 + 1} y1={Y0 - 30} x2={right - 1} y2={Y0 - 30} stroke="#22d3ee" markerStart={`url(#${arrow})`} markerEnd={`url(#${arrow})`} />
      <line x1={X0 - 30} y1={Y0 + 1} x2={X0 - 30} y2={bottom - 1} stroke="#22d3ee" markerStart={`url(#${arrow})`} markerEnd={`url(#${arrow})`} />
      <rect x={(X0 + right) / 2 - 22} y={Y0 - 38} width="44" height="16" fill="#060b16" />
      <text x={(X0 + right) / 2} y={Y0 - 26.5} textAnchor="middle" fontSize="11" className="fill-cyan-300 font-mono">120.0</text>
      <rect x={X0 - 38} y={(Y0 + bottom) / 2 - 18} width="16" height="36" fill="#060b16" />
      <text x={X0 - 26} y={(Y0 + bottom) / 2} textAnchor="middle" fontSize="11" transform={`rotate(-90 ${X0 - 26} ${(Y0 + bottom) / 2})`} className="fill-cyan-300 font-mono">80.0</text>

      {/* Part */}
      <rect x={X0} y={Y0} width={120 * S} height={80 * S} stroke="#cbd5e1" strokeWidth="1.25" fill="rgba(148,163,184,0.03)" />
      {POCKETS.map(([x, y]) => (
        <rect key={x} x={x} y={y} width={38 * S} height={18 * S} stroke="#94a3b8" strokeDasharray="4 3" fill="rgba(6,182,212,0.04)" />
      ))}
      {HOLES.map(([cx, cy]) => (
        <g key={cx}>
          <circle cx={cx} cy={cy} r={R_HOLE} stroke="#94a3b8" />
          <path d={`M${cx - R_HOLE - 4} ${cy} H${cx + R_HOLE + 4} M${cx} ${cy - R_HOLE - 4} V${cy + R_HOLE + 4}`} stroke="#475569" strokeWidth="0.75" />
        </g>
      ))}

      {/* Toolpaths trace once on load */}
      <g stroke="#22d3ee" strokeWidth="1.5" strokeLinejoin="round">
        {POCKETS.map(([x, y], i) => (
          <path key={x} d={zigzag(x, y)} pathLength={1} className="bp-trace" style={{ animationDelay: `${0.6 + i * 1.1}s` }} />
        ))}
        <path d={PROFILE} pathLength={1} className="bp-trace" strokeOpacity="0.7" style={{ animationDelay: '2.8s', animationDuration: '3s' }} />
      </g>
      {HOLES.map(([cx, cy]) => <circle key={cx} cx={cx} cy={cy} r="2.2" fill="#f472b6" />)}

      {/* Recognised features */}
      <Callout from={[POCKETS[1][0] + 38 * S, POCKETS[1][1] + 9 * S]} at={[404, 128]} text="POCKET ×2 · 38×18 · 8 DP" />
      <Callout from={[HOLES[3][0] + R_HOLE, HOLES[3][1]]} at={[404, 232]} text="4× Ø6.8 THRU" />
      <Callout from={[right + 8, bottom - 30]} at={[404, 296]} text="PROFILE 120×80 · T02" />
      <Callout from={[X0 + 4, bottom + 8]} at={[X0 + 40, bottom + 52]} text="STOCK 6061-T6 · 125×85×20" tone="amber" />
    </svg>
  )
}

/** Hero product shot: a GCodr workspace window with the annotated drawing beside the live G-code demo. */
export default function HeroWorkspace() {
  return (
    <div className="relative">
      <div aria-hidden="true" className="pointer-events-none absolute -inset-x-16 -bottom-24 top-1/4 rounded-[50%] bg-[radial-gradient(closest-side,rgba(6,182,212,0.22),rgba(59,130,246,0.08)_60%,transparent)] blur-2xl" />
      <div className="relative overflow-hidden rounded-xl border border-white/10 bg-[#060b16] shadow-[0_40px_120px_-40px_rgba(6,182,212,0.35)]">
        <div className="flex h-10 items-center gap-4 border-b border-white/[0.07] bg-white/[0.02] px-4">
          <div className="flex gap-1.5" aria-hidden="true">
            {[0, 1, 2].map(i => <span key={i} className="h-2.5 w-2.5 rounded-full bg-slate-700" />)}
          </div>
          <div className="flex min-w-0 gap-1 font-mono text-[11px]">
            <span className="rounded-md border border-white/10 bg-white/[0.04] px-2.5 py-1 text-slate-200">bracket_mount.dxf</span>
            <span className="hidden px-2.5 py-1 text-slate-500 sm:inline">bracket_mount.pdf</span>
          </div>
          <span className="ml-auto hidden items-center gap-2 font-mono text-[11px] text-slate-500 md:flex">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />Job 0142 · Program verified
          </span>
        </div>
        <div className="grid md:grid-cols-[1.35fr_1fr]">
          <div className="relative border-b border-white/[0.07] md:border-b-0 md:border-r">
            <BlueprintGrid />
            <div className="relative flex items-center justify-between px-5 pt-4 font-mono text-[10px] uppercase tracking-widest text-slate-500">
              <span>Bracket mount · Sheet 1/1</span>
              <span className="hidden sm:inline">Scale 1:1 · mm</span>
            </div>
            <div className="relative overflow-hidden px-3 pb-4 md:px-5">
              <PartDrawing />
            </div>
          </div>
          <GcodeDemo bare />
        </div>
      </div>
    </div>
  )
}
